import React, { useRef } from "react";
import { ChevronLeft, ChevronRight, Trophy, MessageCircle } from "lucide-react";
import { championships } from "../data/matches";

const WA_NUMBER = "5588988676142";

function buildWaLink(c) {
  const msg = `Olá! Quero assistir os jogos da *${c.name}* pelo UniPlay UHD. Como faço para contratar?`;
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
}

export default function FeaturedMatches() {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 640, behavior: "smooth" });

  return (
    <section
      data-testid="featured-championships"
      className="max-w-[1500px] mx-auto px-6 mt-16"
    >
      <div className="flex items-end justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-600/30">
            <Trophy className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2 text-[11px] text-yellow-400/80 font-bold uppercase tracking-widest mb-1">
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                Todos os campeonatos
              </span>
              <span className="text-white/30">•</span>
              <span className="text-white/60">Assista em Full HD</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">
              Principais Campeonatos de Futebol
            </h2>
            <p className="text-white/60 text-sm mt-1 max-w-2xl">
              Do Brasileirão à Champions League: cobertura completa dos maiores torneios do futebol mundial.
            </p>
          </div>
        </div>
        <div className="hidden md:flex gap-2">
          <button
            onClick={() => scroll(-1)}
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-yellow-500/30 transition border border-white/10 flex items-center justify-center"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={() => scroll(1)}
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-yellow-500/30 transition border border-white/10 flex items-center justify-center"
            aria-label="Próximo"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      <div
        ref={ref}
        className="no-scrollbar flex gap-4 overflow-x-auto pb-4 -mx-2 px-2 snap-x"
      >
        {championships.map((c) => (
          <a
            key={c.id}
            href={buildWaLink(c)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`championship-${c.id}`}
            className="shrink-0 w-[280px] snap-start rounded-2xl overflow-hidden border border-white/10 hover:border-yellow-500/50 hover:-translate-y-1 transition group"
          >
            <div className={`relative bg-gradient-to-br ${c.gradient} p-6 h-52 flex flex-col justify-between overflow-hidden`}>
              {c.image ? (
                <>
                  <img
                    src={c.image}
                    alt={c.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/55" />
                </>
              ) : (
                <div className="absolute inset-0 opacity-20 pointer-events-none">
                  <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/20 blur-2xl" />
                  <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
                </div>
              )}

              <div className="relative flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/50 backdrop-blur-sm text-[10px] font-bold uppercase tracking-widest text-white/90">
                  <Trophy className="w-3 h-3 text-yellow-400" />
                  Campeonato
                </span>
                {!c.image && (
                  <span className={`w-14 h-14 rounded-full ${c.accent} border-4 border-white/20 flex items-center justify-center text-white font-extrabold text-lg tracking-wider shadow-xl`}>
                    {c.badge}
                  </span>
                )}
              </div>

              <div className="relative">
                <h3 className="text-white text-xl font-extrabold leading-tight drop-shadow-lg">
                  {c.name}
                </h3>
                <p className="text-white/85 text-xs mt-1.5 line-clamp-2 drop-shadow">
                  {c.tagline}
                </p>
              </div>
            </div>

            <div className="bg-[#0f0f13] p-4 flex items-center justify-between gap-3">
              <div className="text-[11px] text-white/60 leading-tight min-w-0">
                <p className="uppercase tracking-wider font-bold text-white/40">Onde passa</p>
                <p className="text-white/85 font-semibold truncate">{c.channels}</p>
              </div>
              <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#25D366] text-white text-[11px] font-bold shadow-md group-hover:brightness-110 transition shrink-0">
                <MessageCircle className="w-3.5 h-3.5" /> Contratar
              </span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
